import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DeliveryDetailResponse,
  DeliveryProblem,
  SuccessEnvelope,
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { CodeChip } from '../components/CodeChip'
import { ConfirmActionDialog } from '../components/ConfirmActionDialog'
import { DetailRow } from '../components/DetailRow'
import { RelativeTime } from '../components/RelativeTime'
import { StatusBadge } from '../components/StatusBadge'
import { useAdminMutation } from '../hooks/useAdminMutation'

interface TimelineStep {
  key: string
  label: string
  timestamp: number
  highlight?: 'positive' | 'negative'
}

function buildTimeline(record: DeliveryProblem): TimelineStep[] {
  return [
    { key: 'created',  label: 'Created',     timestamp: record.created_time },
    { key: 'sent',     label: 'Sent',        timestamp: record.sent_time },
    { key: 'first_read', label: 'First read', timestamp: record.first_read_time,
      highlight: 'positive' },
    { key: 'expires', label: 'Expires',      timestamp: record.expires_time },
    { key: 'revoked', label: 'Revoked',      timestamp: record.revoked_time,
      highlight: 'negative' },
  ]
}

export function DeliveryDetailPage() {
  const params = useParams<{ deliveryId: string }>()
  const deliveryId = params.deliveryId ?? ''
  const client = useAdminApiClient()
  const query = useQuery({
    queryKey: queryKeys.deliveryDetail(deliveryId),
    queryFn: () => client.get<DeliveryDetailResponse>(
      `/r/admin/deliveries/${deliveryId}`,
    ),
    enabled: !!deliveryId,
  })

  if (query.isLoading) {
    return (
      <div className="p-6 text-sm text-stone-500">Loading delivery…</div>
    )
  }
  if (query.isError || !query.data?.success) {
    return (
      <div
        className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                   text-red-800"
        role="alert"
      >
        <p className="font-semibold">Failed to load delivery.</p>
        <p className="mt-1 text-xs text-red-700">
          The delivery may not exist or the backend returned an error.
        </p>
        <Link
          to="/deliveries"
          className="mt-3 inline-block rounded-md border border-red-300
                     bg-white px-3 py-1 text-xs font-medium text-red-700
                     hover:bg-red-50"
        >
          Back to delivery problems
        </Link>
      </div>
    )
  }
  const record = query.data.data

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            <CodeChip>{record.delivery_id}</CodeChip>
          </h1>
          <Link
            to="/deliveries"
            className="text-xs text-accent hover:underline"
          >
            ← Back to delivery problems
          </Link>
        </div>
        <StatusBadge kind="delivery" value={record.status} />
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
            Identity
          </h2>
          <dl className="mt-2 divide-y divide-stone-100">
            <DetailRow label="Recipient name">
              {record.recipient_name || <span className="text-stone-400">—</span>}
            </DetailRow>
            <DetailRow label="Recipient domain">
              <CodeChip>{record.recipient_email_domain}</CodeChip>
            </DetailRow>
            <DetailRow label="Linked user">
              <Link
                className="text-accent hover:underline"
                to={`/death-confirmation/${record.link_account}`}
              >
                <CodeChip>{record.link_account}</CodeChip>
              </Link>
            </DetailRow>
            <DetailRow label="Linked message">
              <details>
                <summary className="cursor-pointer text-xs text-stone-500
                                    hover:text-stone-800">
                  Show message id
                </summary>
                <div className="mt-1">
                  <CodeChip>{record.message_id}</CodeChip>
                </div>
              </details>
            </DetailRow>
            <DetailRow label="Provider message id">
              <CodeChip>{record.provider_message_id}</CodeChip>
            </DetailRow>
          </dl>
        </section>

        <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
            Status timeline
          </h2>
          <ol className="mt-2 space-y-2 text-sm">
            {buildTimeline(record).map((step) => (
              <li
                key={step.key}
                data-testid={`timeline-${step.key}`}
                className="flex items-baseline gap-3"
              >
                <span
                  className={[
                    'inline-block h-2 w-2 rounded-full',
                    step.timestamp
                      ? step.highlight === 'negative'
                        ? 'bg-red-500'
                        : 'bg-emerald-500'
                      : 'bg-stone-300',
                  ].join(' ')}
                  aria-hidden
                />
                <span className="w-28 shrink-0 text-xs uppercase
                                 tracking-wide text-stone-500">
                  {step.label}
                </span>
                <span className="text-stone-800">
                  {step.timestamp
                    ? <RelativeTime timestamp={step.timestamp} />
                    : <span className="text-xs text-stone-400">not yet</span>}
                </span>
              </li>
            ))}
          </ol>
          {record.last_error && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50
                            p-2 text-xs text-red-800">
              <span className="font-medium">Last error:</span>{' '}
              {record.last_error}
            </div>
          )}
          {record.revoked_reason && (
            <div className="mt-3 text-xs text-stone-700">
              <span className="font-medium">Revoke reason:</span>{' '}
              <span className="whitespace-pre-wrap">{record.revoked_reason}</span>
            </div>
          )}
        </section>

        <ManualActionsCard record={record} />
      </div>
    </div>
  )
}

function ManualActionsCard({ record }: { record: DeliveryProblem }) {
  const auth = useAuth()
  const canOperate = auth.requireRole('OPERATOR')
  const [showRevokeDialog, setShowRevokeDialog] = useState(false)

  // Revoke is irreversible at the data-model level — once a recipient
  // link is invalidated it cannot be un-revoked. Plan 06's
  // "confirm twice" pattern guards exactly this kind of action.
  const revokeMutation = useAdminMutation<{ reason: string }, DeliveryProblem>({
    mutate: (client, { reason }) =>
      client.post<SuccessEnvelope<DeliveryProblem>>(
        `/r/admin/deliveries/${record.delivery_id}/revoke`,
        { reason },
      ),
    invalidates: [
      queryKeys.deliveryDetail(record.delivery_id),
      // The list query key is parameterised; invalidating the
      // top-level prefix refreshes every cached page variant.
      ['deliveryProblems'],
      queryKeys.health(),
    ],
  })

  const alreadyRevoked = record.status === 'REVOKED'

  return (
    <section
      className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm
                 lg:col-span-2"
    >
      <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
        Manual actions
      </h2>
      {!canOperate && (
        <p className="mt-2 text-sm text-stone-600" data-testid="viewer-notice">
          Operator role required to perform manual actions on this delivery.
          Sign in as an operator (or higher) to enable Revoke.
        </p>
      )}
      {canOperate && (
        <>
          <p className="mt-2 text-sm text-stone-600">
            Revoke invalidates the recipient link and writes a
            {' '}<CodeChip>REVOKE_DELIVERY</CodeChip> entry to the audit
            log with the reason. This action cannot be reversed.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={alreadyRevoked}
              title={alreadyRevoked
                ? 'This delivery is already revoked'
                : 'Invalidate the recipient link (irreversible)'}
              onClick={() => setShowRevokeDialog(true)}
              className={
                'rounded-md px-3 py-1 text-xs font-medium shadow-sm ' +
                (alreadyRevoked
                  ? 'cursor-not-allowed border border-stone-200 bg-stone-50 text-stone-500'
                  : 'border border-red-300 bg-white text-red-700 hover:bg-red-50')
              }
            >
              Revoke recipient link
            </button>
            <button
              type="button"
              disabled
              title="Available in a later wave"
              className="cursor-not-allowed rounded-md border border-stone-200
                         bg-stone-50 px-3 py-1 text-xs text-stone-500"
            >
              Resend
            </button>
            {/* plan-admin/07: per-record revoke is the surgical tool;
                Stop Delivery is the cascade tool when the operator
                already knows the *user* shouldn't be receiving any
                delivery. The cross-link sends the operator to the
                User Detail page rather than triggering the cascade
                from here, so the audit row stays attached to the
                correct target_type=USER and the operator re-confirms
                the larger action with full context. */}
            <Link
              to={`/death-confirmation/${record.link_account}?tab=manual`}
              data-testid="stop-delivery-cross-link"
              className="rounded-md border border-stone-300 bg-white px-3 py-1
                         text-xs font-medium text-stone-700 shadow-sm
                         hover:bg-stone-50"
            >
              Stop delivery for this user…
            </Link>
          </div>
        </>
      )}

      {showRevokeDialog && (
        <ConfirmActionDialog
          title="Revoke recipient link"
          body={
            <span>
              Invalidates the recipient link for delivery
              {' '}<CodeChip>{record.delivery_id}</CodeChip>. The
              recipient will not be able to use this link again, even
              if they kept the original email. The audit log row will
              include the reason verbatim.
            </span>
          }
          confirmLabel="Revoke"
          confirmTwice
          secondConfirmBody="Revoke is permanent. Click Confirm revoke to invalidate this link."
          onConfirm={async ({ reason }) => {
            await revokeMutation.mutateAsync({ reason })
            setShowRevokeDialog(false)
          }}
          onCancel={() => setShowRevokeDialog(false)}
        />
      )}
    </section>
  )
}
